import { useState } from 'react';
import { Play, Pause, Square, Settings, LayoutGrid, ExternalLink } from 'lucide-react';
import { useTimer } from '@/lib/timer-context';
import { TaskCompletionWidget } from './TaskCompletionWidget';
import { ProgressWidget } from './ProgressWidget';
import { ActivityLog } from './ActivityLog';
import { RecentTasks } from './RecentTasks';
import { WeeklyCapacityWidget } from './WeeklyCapacityWidget';
import { WidgetWrapper } from './WidgetWrapper';
import { TaskDetailModal } from './TaskDetailModal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Link } from 'react-router-dom';

// Helper to format seconds to HH:MM:SS
function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// Helper to format seconds to readable time (e.g., 8h 30m)
function formatReadableTime(seconds: number): string {
  if (seconds === 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

interface Widget {
  id: string;
  name: string;
  visible: boolean;
  order: number;
}

// Sample tasks for demonstration
const sampleTasks = [
  { id: '1', title: 'Design Homepage Banner', project: 'Website Redesign', status: 'In Progress' as const, timeLogged: '3h 45m', priority: 'high' as const },
  { id: '2', title: 'Mobile App UI Components', project: 'Mobile App', status: 'In Progress' as const, timeLogged: '5h 30m', priority: 'medium' as const },
  { id: '3', title: 'Content Strategy Document', project: 'Brand Strategy', status: 'Quality Check' as const, timeLogged: '2h 15m', priority: 'low' as const },
  { id: '4', title: 'User Research Analysis', project: 'UX Research', status: 'Not Started' as const, timeLogged: '0h 0m', priority: 'medium' as const },
];

export function FigmakeDashboard() {
  const { isRunning, elapsedSeconds, selectedTask, pauseTimer, resumeTimer, stopTimer } = useTimer();
  
  // Track pause state locally since context doesn't expose it
  const [isPaused, setIsPaused] = useState(false);
  
  // Widget management state
  const [isWidgetEditMode] = useState(false);
  const [widgets, setWidgets] = useState<Widget[]>([
    { id: 'timeTracking', name: 'Time Tracking', visible: true, order: 0 },
    { id: 'taskCompletion', name: 'Task Completion', visible: true, order: 1 },
    { id: 'progress', name: 'Weekly Progress', visible: true, order: 2 },
    { id: 'activity', name: 'Activity Log', visible: true, order: 3 },
    { id: 'recentTasks', name: 'Recent Tasks', visible: true, order: 4 },
    { id: 'weeklyCapacity', name: 'Weekly Capacity', visible: false, order: 5 },
  ]);
  
  // Task detail modal
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  
  // Sample tracking data - in production this would come from context/backend
  const trackedTodaySeconds = elapsedSeconds + (2 * 3600); // Add 2 hours of "previous" work
  const trackedThisWeekSeconds = trackedTodaySeconds + (15 * 3600); // Add 15 hours of "previous" work
  
  const tasksToday = { completed: 3, total: 5 };
  const tasksThisWeek = { completed: 12, total: 18 };
  
  // Daily goal in seconds (8 hours)
  const dailyGoalSeconds = 8 * 60 * 60;
  const weeklyGoalSeconds = 40 * 60 * 60;
  
  // Calculate progress
  const todayProgress = Math.min((trackedTodaySeconds / dailyGoalSeconds) * 100, 100);
  const weekProgress = Math.min((trackedThisWeekSeconds / weeklyGoalSeconds) * 100, 100);
  
  // Visible widgets
  const visibleWidgets = widgets.filter(w => w.visible).sort((a, b) => a.order - b.order);
  
  // Toggle widget visibility
  const toggleWidgetVisibility = (id: string) => {
    setWidgets(prev => prev.map(w => 
      w.id === id ? { ...w, visible: !w.visible } : w
    ));
  };
  
  // Handle timer controls
  const handlePauseResume = () => {
    if (isPaused) {
      resumeTimer();
      setIsPaused(false);
    } else {
      pauseTimer();
      setIsPaused(true);
    }
  };
  
  const handleStop = () => {
    stopTimer();
    setIsPaused(false);
  };
  
  const handleTaskClick = (task: any) => {
    setSelectedTaskDetail({
      ...task,
      description: 'Click "Start Timer" to begin tracking time on this task.',
    });
    setIsTaskModalOpen(true);
  };
  
  const handleStartTimerFromModal = async (_task: any) => {
    // In a real implementation, you would fetch the actual task from the backend
    // For now, we just close the modal
    setIsTaskModalOpen(false);
  };
  
  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-white border-b border-[#e6e6e6] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#e30840] flex items-center justify-center">
              <LayoutGrid className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[18px] font-semibold text-[#262626]">Dashboard</h1>
              <p className="text-[12px] text-[#808080]">Time tracking & task management</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Widget Settings */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-[12px]">
                  <Settings className="w-4 h-4 mr-2" />
                  Widgets
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48">
                <DropdownMenuLabel>Toggle Widgets</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {widgets.map(widget => (
                  <DropdownMenuCheckboxItem
                    key={widget.id}
                    checked={widget.visible}
                    onCheckedChange={() => toggleWidgetVisibility(widget.id)}
                  >
                    {widget.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Link to Timesheet */}
            <Link to="/figmake/timesheet">
              <Button size="sm" className="text-[12px] bg-[#e30840] hover:bg-[#c50735]">
                <ExternalLink className="w-4 h-4 mr-2" />
                Timesheet
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="p-6">
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Today's Time */}
          <div className="bg-white rounded-md shadow-sm p-4">
            <p className="text-[10px] text-[#808080] uppercase tracking-wide mb-1">Today</p>
            <p className="text-[24px] font-bold text-[#262626]">{formatReadableTime(trackedTodaySeconds)}</p>
            <div className="h-1 bg-[#f5f5f5] rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-[#e30840] transition-all duration-300 rounded-full"
                style={{ width: `${todayProgress}%` }}
              />
            </div>
          </div>
          
          {/* This Week's Time */}
          <div className="bg-white rounded-md shadow-sm p-4">
            <p className="text-[10px] text-[#808080] uppercase tracking-wide mb-1">This Week</p>
            <p className="text-[24px] font-bold text-[#262626]">{formatReadableTime(trackedThisWeekSeconds)}</p>
            <div className="h-1 bg-[#f5f5f5] rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-[#4A90E2] transition-all duration-300 rounded-full"
                style={{ width: `${weekProgress}%` }}
              />
            </div>
          </div>
          
          {/* Tasks Today */}
          <div className="bg-white rounded-md shadow-sm p-4">
            <p className="text-[10px] text-[#808080] uppercase tracking-wide mb-1">Tasks Today</p>
            <p className="text-[24px] font-bold text-[#262626]">
              {tasksToday.completed}<span className="text-[14px] font-normal text-[#808080]">/{tasksToday.total}</span>
            </p>
            <div className="h-1 bg-[#f5f5f5] rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-[#27AE60] transition-all duration-300 rounded-full"
                style={{ width: `${tasksToday.total > 0 ? (tasksToday.completed / tasksToday.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          
          {/* Tasks This Week */}
          <div className="bg-white rounded-md shadow-sm p-4">
            <p className="text-[10px] text-[#808080] uppercase tracking-wide mb-1">Tasks This Week</p>
            <p className="text-[24px] font-bold text-[#262626]">
              {tasksThisWeek.completed}<span className="text-[14px] font-normal text-[#808080]">/{tasksThisWeek.total}</span>
            </p>
            <div className="h-1 bg-[#f5f5f5] rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-[#F5A623] transition-all duration-300 rounded-full"
                style={{ width: `${tasksThisWeek.total > 0 ? (tasksThisWeek.completed / tasksThisWeek.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
        
        {/* Time Tracking Widget - Always prominent */}
        {visibleWidgets.some(w => w.id === 'timeTracking') && (
          <div className="bg-white rounded-md shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[12px] text-[#808080] uppercase tracking-wide">Active Timer</p>
              {isRunning && !isPaused && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#27AE60] animate-pulse" />
                  <span className="text-[11px] text-[#27AE60] font-medium">Running</span>
                </span>
              )}
              {isPaused && (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#F5A623]" />
                  <span className="text-[11px] text-[#F5A623] font-medium">Paused</span>
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-6">
              {/* Timer Display */}
              <div className="flex items-center gap-4">
                <div className="w-[120px] h-[120px] relative">
                  {/* Circular progress */}
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#f5f5f5"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#e30840"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${todayProgress * 2.83} 283`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[20px] font-bold text-[#262626] tabular-nums">
                      {formatTime(elapsedSeconds)}
                    </span>
                    <span className="text-[10px] text-[#808080]">Current</span>
                  </div>
                </div>
              </div>
              
              {/* Task Selection & Controls */}
              <div className="flex-1">
                {/* Selected Task */}
                <div className="mb-4">
                  <p className="text-[11px] text-[#808080] mb-1">Current Task</p>
                  {selectedTask ? (
                    <div className="p-3 bg-[#fafafa] rounded-md">
                      <p className="text-[13px] font-medium text-[#262626]">{selectedTask.title}</p>
                      <p className="text-[11px] text-[#e30840]">{(selectedTask as any).project || (selectedTask as any).project_name || 'No project'}</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-[#fafafa] rounded-md border-2 border-dashed border-[#e6e6e6]">
                      <p className="text-[12px] text-[#808080]">No task selected</p>
                      <p className="text-[10px] text-[#b3b3b3]">Select from Recent Tasks below</p>
                    </div>
                  )}
                </div>
                
                {/* Timer Controls */}
                <div className="flex items-center gap-2">
                  {!isRunning ? (
                    <Button
                      disabled={!selectedTask}
                      className="bg-[#e30840] hover:bg-[#c50735] text-white"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handlePauseResume}
                        variant="outline"
                        className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-white"
                      >
                        {isPaused ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                        {isPaused ? 'Resume' : 'Pause'}
                      </Button>
                      <Button
                        onClick={handleStop}
                        variant="outline"
                        className="border-[#e30840] text-[#e30840] hover:bg-[#e30840] hover:text-white"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Widget Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleWidgets.some(w => w.id === 'taskCompletion') && (
            <WidgetWrapper isEditMode={isWidgetEditMode}>
              <TaskCompletionWidget
                tasksCompletedToday={0}
                totalTasksToday={5}
                tasksCompletedThisWeek={3}
                totalTasksThisWeek={15}
              />
            </WidgetWrapper>
          )}
          
          {visibleWidgets.some(w => w.id === 'progress') && (
            <WidgetWrapper isEditMode={isWidgetEditMode}>
              <ProgressWidget
                totalHours={trackedThisWeekSeconds / 3600}
                targetHours={40}
              />
            </WidgetWrapper>
          )}
          
          {visibleWidgets.some(w => w.id === 'activity') && (
            <WidgetWrapper isEditMode={isWidgetEditMode}>
              <ActivityLog />
            </WidgetWrapper>
          )}
          
          {visibleWidgets.some(w => w.id === 'recentTasks') && (
            <div className="col-span-2 lg:col-span-1">
              <WidgetWrapper isEditMode={isWidgetEditMode}>
                <RecentTasks 
                  tasks={sampleTasks}
                  onTaskClick={handleTaskClick}
                />
              </WidgetWrapper>
            </div>
          )}
          
          {visibleWidgets.some(w => w.id === 'weeklyCapacity') && (
            <div className="col-span-2 lg:col-span-3">
              <WidgetWrapper isEditMode={isWidgetEditMode}>
                <WeeklyCapacityWidget />
              </WidgetWrapper>
            </div>
          )}
        </div>
      </div>
      
      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTaskDetail}
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        onStartTimer={handleStartTimerFromModal}
      />
    </div>
  );
}
